import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, Send, FileText, UserPlus, Search, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const PRIORITY_LABEL = { 1: 'P1 — Critical', 2: 'P2 — High', 3: 'P3 — Moderate', 4: 'P4 — Low' };

/**
 * Reusable "Submit a form request" dialog. Available to every signed-in
 * user; visibility of forms/patients is scoped server-side by role.
 *
 * Flow:
 *   1. Pick a form from those assigned to the org.
 *   2. Pick a patient from those accessible to the current user.
 *      (Or click "Patient not listed?" to request a new patient — admins
 *      will review and create the patient record).
 *   3. Fill the form fields inline (SimpleFieldRenderer).
 *   4. Submit → creates a service_request + form_submission. If a new
 *      patient was requested, admins see the captured details on the
 *      request detail page.
 */
export default function SubmitFormRequestDialog({ open, onClose, onSubmitted }) {
  const { user } = useAuth();
  const isServiceProvider = user?.role === 'service_provider';

  const [forms, setForms] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);

  const [formId, setFormId] = useState('');
  const [selectedForm, setSelectedForm] = useState(null);
  const [patientId, setPatientId] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [requestNew, setRequestNew] = useState(false);
  const [newPatient, setNewPatient] = useState({});
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState(3);
  const [description, setDescription] = useState('');
  const [formData, setFormData] = useState({});
  const [busy, setBusy] = useState(false);

  // Reset on open.
  useEffect(() => {
    if (!open) return;
    setFormId(''); setSelectedForm(null);
    setPatientId(''); setPatientSearch(''); setRequestNew(false); setNewPatient({});
    setTitle(''); setPriority(3); setDescription(''); setFormData({});
    (async () => {
      setLoadingLists(true);
      try {
        const [fRes, pRes] = await Promise.all([
          axios.get(`${API_URL}/api/forms`, { headers: authHeader() }).catch(() => ({ data: { forms: [] } })),
          axios.get(`${API_URL}/api/patients?limit=200`, { headers: authHeader() }).catch(() => ({ data: [] })),
        ]);
        const formsList = fRes.data?.forms || fRes.data || [];
        setForms(Array.isArray(formsList) ? formsList : []);
        const ptsRaw = pRes.data?.patients || pRes.data || [];
        setPatients(Array.isArray(ptsRaw) ? ptsRaw : []);
      } finally { setLoadingLists(false); }
    })();
  }, [open]);

  // Load full form (fields) when picked.
  useEffect(() => {
    setSelectedForm(null);
    setFormData({});
    if (!formId) return;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/forms/${formId}`, { headers: authHeader() });
        setSelectedForm(r.data);
        if (!title) setTitle(`${r.data.name} request`);
      } catch (err) {
        toast.error('Failed to load form');
      }
    })();
    /* eslint-disable-next-line */
  }, [formId]);

  const filteredPatients = patientSearch
    ? patients.filter(p => {
        const n = (p.name || `${p.first_name || ''} ${p.last_name || ''}`).toLowerCase();
        return n.includes(patientSearch.toLowerCase()) || (p.mrn || '').toLowerCase().includes(patientSearch.toLowerCase());
      })
    : patients;

  const submit = async () => {
    if (!formId) { toast.error('Pick a form'); return; }
    if (!title.trim()) { toast.error('Add a short title'); return; }
    if (!patientId && !requestNew) {
      toast.error('Pick a patient or request a new one');
      return;
    }
    if (requestNew && !(newPatient.first_name || '').trim()) {
      toast.error('Enter the new patient\'s first name');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        form_id: formId,
        title,
        description,
        priority,
        // Service providers REQUEST forms be filled — they never fill
        // them. Strip any captured form_data so the backend creates an
        // unfilled request that an admin/clinician can complete.
        form_data: isServiceProvider ? null : formData,
        patient_id: patientId || null,
        new_patient: requestNew && !patientId ? newPatient : null,
      };
      await axios.post(`${API_URL}/api/service-requests`, payload, { headers: authHeader() });
      toast.success('Request submitted');
      onSubmitted && onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="submit-form-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#0E7490]" />
            {isServiceProvider ? 'Request a form be filled out' : 'Submit a form request'}
          </DialogTitle>
          {isServiceProvider && (
            <p className="text-xs text-[#64748B] mt-1" data-testid="sp-helper-text">
              As a Service Provider you ask the care team to complete a form on a patient. You won&apos;t fill the form yourself — the assignee will.
            </p>
          )}
        </DialogHeader>

        {loadingLists ? (
          <div className="py-12 text-center text-[#64748B] text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading forms &amp; patients…</div>
        ) : (
          <div className="space-y-4 text-sm">
            {/* Form */}
            <div>
              <Label>Form (assigned to your organization) *</Label>
              <select
                value={formId} onChange={(e) => setFormId(e.target.value)}
                className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-2 bg-white"
                data-testid="form-select"
              >
                <option value="">Pick a form…</option>
                {forms.map(f => <option key={f.id || f._id} value={f.id || f._id}>{f.name || f.title}</option>)}
              </select>
              {forms.length === 0 && (
                <p className="text-[10px] text-amber-700 mt-1">No forms assigned yet. Ask your administrator to publish one.</p>
              )}
            </div>

            {/* Patient picker */}
            <div>
              <Label>Patient *</Label>
              <div className="flex items-center gap-2 mt-1">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8]" />
                  <Input
                    value={patientSearch} onChange={(e) => setPatientSearch(e.target.value)}
                    placeholder="Search patients you have access to…"
                    className="pl-7 h-8 text-sm"
                    data-testid="patient-search"
                    disabled={requestNew}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => { setRequestNew(v => !v); if (!requestNew) setPatientId(''); }}
                  className={`text-xs inline-flex items-center gap-1 px-2 py-1.5 rounded border ${requestNew ? 'bg-[#0E7490] text-white border-[#0E7490]' : 'bg-white text-[#0E7490] border-[#0E7490]/30 hover:bg-[#0E7490]/5'}`}
                  data-testid="toggle-request-new-patient"
                >
                  <UserPlus className="w-3.5 h-3.5" /> {requestNew ? 'Cancel new patient' : 'Patient not in system?'}
                </button>
              </div>

              {!requestNew && (
                <div className="mt-2 max-h-40 overflow-y-auto border border-[#E2E8F0] rounded-md bg-white" data-testid="patient-list">
                  {filteredPatients.length === 0 ? (
                    <div className="text-center text-xs text-[#94A3B8] py-3">No patients match your search.</div>
                  ) : filteredPatients.slice(0, 30).map(p => {
                    const id = p.id || p._id;
                    const name = p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim();
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setPatientId(id)}
                        className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[#F8FAFC] flex items-center justify-between ${patientId === id ? 'bg-[#0E7490]/10' : ''}`}
                        data-testid={`patient-option-${id}`}
                      >
                        <span className="text-[#0A2342] font-medium">{name || '(no name)'}</span>
                        <span className="text-[10px] text-[#94A3B8]">{p.mrn || ''}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {requestNew && (
                <Card className="mt-2 border-[#0E7490]/30 bg-[#0E7490]/5" data-testid="new-patient-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs text-[#0E7490] flex items-center gap-2">
                      <UserPlus className="w-3.5 h-3.5" /> Request a new patient be added
                      <button type="button" onClick={() => setRequestNew(false)} className="ml-auto text-[#94A3B8] hover:text-rose-600" data-testid="cancel-new-patient">
                        <X className="w-3 h-3" />
                      </button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-2">
                    <NPField label="First name *" v={newPatient.first_name} onChange={(v) => setNewPatient({ ...newPatient, first_name: v })} testId="np-first-name" />
                    <NPField label="Last name" v={newPatient.last_name} onChange={(v) => setNewPatient({ ...newPatient, last_name: v })} testId="np-last-name" />
                    <NPField label="Date of birth" type="date" v={newPatient.dob} onChange={(v) => setNewPatient({ ...newPatient, dob: v })} testId="np-dob" />
                    <NPField label="MRN (optional)" v={newPatient.mrn} onChange={(v) => setNewPatient({ ...newPatient, mrn: v })} testId="np-mrn" />
                    <NPField label="Phone" v={newPatient.phone} onChange={(v) => setNewPatient({ ...newPatient, phone: v })} testId="np-phone" />
                    <NPField label="Email" v={newPatient.email} onChange={(v) => setNewPatient({ ...newPatient, email: v })} testId="np-email" />
                    <div className="col-span-2">
                      <NPField label="Notes for admin (insurance, address, urgency…)" v={newPatient.notes} onChange={(v) => setNewPatient({ ...newPatient, notes: v })} testId="np-notes" textarea />
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Title / Priority */}
            <div className="grid grid-cols-[1fr_140px] gap-3">
              <div>
                <Label>Title *</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short summary of what you need" data-testid="title-input" />
              </div>
              <div>
                <Label>Priority</Label>
                <select
                  value={priority} onChange={(e) => setPriority(parseInt(e.target.value, 10))}
                  className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-2 bg-white"
                  data-testid="priority-select"
                >
                  {Object.entries(PRIORITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label>Notes (optional)</Label>
              <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full mt-1 rounded-md border border-[#E2E8F0] px-3 py-2 text-sm" placeholder="Extra context, urgency, due dates…" data-testid="description-input" />
            </div>

            {/* Inline form fields — hidden for service_providers, who
                REQUEST forms be filled rather than filling them. */}
            {!isServiceProvider && selectedForm && (selectedForm.fields || []).length > 0 && (
              <Card className="border-[#0E7490]/30 bg-[#0E7490]/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs text-[#0E7490] flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5" /> {selectedForm.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(selectedForm.fields || []).slice(0, 12).map((f, i) => (
                    <SimpleFieldRenderer
                      key={f.id || f.key || i}
                      field={f}
                      value={formData[f.id || f.key || `f${i}`] || ''}
                      onChange={(v) => setFormData(prev => ({ ...prev, [f.id || f.key || `f${i}`]: v }))}
                    />
                  ))}
                  {(selectedForm.fields || []).length > 12 && (
                    <p className="text-[10px] text-[#94A3B8]">+ {selectedForm.fields.length - 12} more fields will be available on the full form.</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || loadingLists || !formId || !title.trim() || (!patientId && !requestNew)}
            className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white"
            data-testid="confirm-submit-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NPField({ label, v, onChange, testId, type = 'text', textarea }) {
  return (
    <div>
      <Label className="text-[11px]">{label}</Label>
      {textarea ? (
        <textarea rows={2} value={v || ''} onChange={(e) => onChange(e.target.value)} className="w-full rounded-md border border-[#E2E8F0] px-2 py-1.5 text-xs bg-white" data-testid={testId} />
      ) : (
        <Input type={type} value={v || ''} onChange={(e) => onChange(e.target.value)} className="h-8 text-xs bg-white" data-testid={testId} />
      )}
    </div>
  );
}

function SimpleFieldRenderer({ field, value, onChange }) {
  const lbl = field.label || field.name || field.id;
  const type = (field.type || 'text').toLowerCase();
  if (type === 'textarea') {
    return (
      <div>
        <Label className="text-xs">{lbl}{field.required ? ' *' : ''}</Label>
        <textarea rows={2} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-sm bg-white" />
      </div>
    );
  }
  if (type === 'select' || type === 'dropdown') {
    return (
      <div>
        <Label className="text-xs">{lbl}{field.required ? ' *' : ''}</Label>
        <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white text-sm">
          <option value="">—</option>
          {(field.options || []).map((o, i) => (
            <option key={i} value={typeof o === 'string' ? o : o.value}>{typeof o === 'string' ? o : o.label || o.value}</option>
          ))}
        </select>
      </div>
    );
  }
  if (type === 'checkbox' || type === 'bool' || type === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-xs text-[#0A2342]">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        {lbl}
      </label>
    );
  }
  return (
    <div>
      <Label className="text-xs">{lbl}{field.required ? ' *' : ''}</Label>
      <Input type={type === 'date' ? 'date' : type === 'number' ? 'number' : 'text'} value={value} onChange={(e) => onChange(e.target.value)} className="h-8 text-sm bg-white" />
    </div>
  );
}
